import { useState, useEffect, useRef } from "react";
import { db, storage, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { collection, doc, setDoc, updateDoc, onSnapshot, query, orderBy, deleteDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth, getIdTokenResult } from "firebase/auth";

import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import PointV2 from "./PointV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Point.module.css";
import ErrMsg from "../ErrMsg";
import { BlockButton, ConfirmButton, ListButton, TextButton } from "../components/Buttons";
import { PointType, usePoint } from "../PointContext";
import { useMember } from "../MemberContext";
import { PhotoInput, photoResize, ToggleSwitch, BlockInput } from "../components/Inputs";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

export interface WithdrawType {
    id: string;
    title: string;
    amount: string;
    type: string;
    image: string;
    active: boolean;
    timestamp?: number;
}

const Point = () => {
    const { version } = useDesignVersion();
    const [addPoint, setAddPoint] = useState<boolean>(false);
    const [addWithdraw, setAddWithdraw] = useState<boolean>(false);
    const [editWithdraw, setEditWithdraw] = useState<boolean>(false);
    const [targetWithdraw, setTargetWithdraw] = useState<WithdrawType | null>(null);

    if (version === "v2") {
        return <PointV2 />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>포인트 관리</h4>
                    <p className={common["text--copy"]}>임의의 계정에 포인트를 지급할 수 있습니다.</p>
                    <button className={style["button--add-point"]} onClick={() => setAddPoint(true)}>
                        포인트 지급하기
                    </button>
                    <h4 className={common["text--title"]}>포인트 방법</h4>
                    <p className={common["text--copy"]}>포인트 환전 방법을 관리할 수 있습니다.</p>
                    <button className={style["button--add-point"]} onClick={() => setAddWithdraw(true)}>
                        환전 방법 추가
                    </button>
                    <WithdrawTable
                        setEditWithdraw={() => setEditWithdraw(true)}
                        setTargetWithdraw={setTargetWithdraw}
                    />
                    <h4 className={common["text--title"]} style={{ marginTop: 24 }}>
                        포인트 환전 승인
                    </h4>
                    <p className={common["text--copy"]}>포인트 출금을 승인 할 수 있습니다.</p>
                    <PointTable />
                </div>
            </div>

            {addPoint && <AddPoint close={() => setAddPoint(false)} />}
            {addWithdraw && <AddWithdraw close={() => setAddWithdraw(false)} />}
            {editWithdraw && (
                <EditWithdraw withdraw={targetWithdraw as WithdrawType} close={() => setEditWithdraw(false)} />
            )}
        </>
    );
};

const PointTable = () => {
    const { withdraws } = usePoint();

    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});

    const confirmWithdraw = async (point: PointType) => {
        const pointId = point.id;

        setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: true }));

        return httpsCallable(
            functions,
            "completeWithdraw"
        )({ docId: pointId })
            .then(() => {
                setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: false }));
            })
            .catch((err) => {
                console.error(err);
                setLoadingConfirmMap((prev) => ({ ...prev, [pointId]: false }));

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <>
            {withdraws && (
                <>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>전화번호</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>요청일시</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>요청금액</p>
                        <p
                            className={[
                                style["text--eyebrow"],
                                style["text--eyebrow--title"],
                                style["text--eyebrow--number"],
                            ].join(" ")}
                        >
                            완료버튼
                        </p>
                    </div>
                    <div className={style["wrapper--point-list"]}>
                        {withdraws.map((point) => (
                            <div key={point.id} className={[style["unit--point-list"]].join(" ")}>
                                <div className={style["container--rows"]}>
                                    <p className={style["text--eyebrow"]}>{point.member?.name ?? "이름 없음"}</p>
                                    <p className={style["text--eyebrow"]}>{formatPhone(point.member?.phone) ?? "-"}</p>
                                    <p className={style["text--eyebrow"]}>
                                        {new Date(point.timestamp).toLocaleString()}
                                    </p>
                                    <p className={[style["text--eyebrow"]].join(" ")}>
                                        {point.pointDelta ? `₩${point.pointDelta.toLocaleString()}` : "-"}
                                    </p>
                                    <ConfirmButton
                                        text="완료"
                                        loading={loadingConfirmMap[point.id] ?? false}
                                        onClick={() => confirmWithdraw(point)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const AddPoint = ({ close }: { close: () => void }) => {
    const { members } = useMember();

    const [search, setSearch] = useState<string>("");

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>포인트 지급하기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay"]}>
                        <div className={style["wrapper--overlay--inputs"]}>
                            <h4 className={common["text--overlay"]}>유저 정보 입력</h4>

                            <input
                                className={style["input--member-filter"]}
                                type="text"
                                placeholder="이름 또는 전화번호로 검색"
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                }}
                            />
                            <div className={style["container--members"]}>
                                <div className={style["unit--member-title"]}>
                                    <p className={style["text--member"]}>이름</p>
                                    <p className={style["text--member"]}>전화번호</p>
                                    <p className={style["text--member"]}>포인트 입력</p>
                                </div>
                                {members
                                    .filter((member) => {
                                        return (
                                            search === "" ||
                                            (search &&
                                                (member.name.toLowerCase().includes(search.toLowerCase()) ||
                                                    member.phone.toLowerCase().includes(search.toLowerCase()) ||
                                                    formatPhone(member.phone)!
                                                        .toLowerCase()
                                                        .includes(search.toLowerCase())))
                                        );
                                    })
                                    .map((member) => (
                                        <MemberList key={member.uid} member={member} />
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                </div>
            </div>
        </div>
    );
};

const MemberList = ({ member }: { member: MemberType }) => {
    const partnerId = sessionStorage.getItem("partnerId");
    const [point, setPoint] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const givePoint = async () => {
        if (!point) {
            alert("지급할 포인트를 입력해주세요.");
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "addPoint"
        )({ uid: member.uid, pointDelta: point, partnerId: partnerId })
            .then(() => {
                setPoint(null);
                setLoading(false);
                alert("포인트가 지급되었습니다.");
                return true;
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <div className={style["unit--member-list"]}>
            <p className={style["text--member"]}>
                {member.name}
                {member.name.length > 3 && <span>{member.name}</span>}
            </p>
            <p className={style["text--member"]}>{member.phone ? formatPhone(member.phone) : "전화번호 없음"}</p>
            <input
                type="number"
                className={style["input--member-point"]}
                placeholder="지급할 포인트 입력"
                value={point ? point : ""}
                onChange={(e) => {
                    setPoint(e.target.value ? parseInt(e.target.value) : 0);
                }}
            />
            <ConfirmButton loading={loading} onClick={givePoint} text="지급" />
        </div>
    );
};

const useWithdraws = () => {
    const partnerId = sessionStorage.getItem("partnerId");
    const [withdraws, setWithdraws] = useState<WithdrawType[]>([]);

    useEffect(() => {
        if (!partnerId) return;
        const unsubscribe = onSnapshot(query(collection(db, `partner/${partnerId}/withdraw`)), (snapshot) => {
            const list = snapshot.docs.map((doc) => {
                const data = doc.data() as WithdrawType;
                return {
                    ...data,
                    id: doc.id, // Firestore 문서 ID로 덮어쓰기
                };
            });
            setWithdraws(list.sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0)));
        });
        return () => unsubscribe();
    }, [partnerId]);

    return { withdraws };
};

const WithdrawTable = ({
    setEditWithdraw,
    setTargetWithdraw,
}: {
    setEditWithdraw: () => void;
    setTargetWithdraw: (withdraw: WithdrawType) => void;
}) => {
    const { withdraws } = useWithdraws();
    const [loadingToggleMap, setLoadingToggleMap] = useState<Record<string, boolean>>({});

    const editInfo = (withdraw: WithdrawType) => {
        setTargetWithdraw(withdraw);
        setEditWithdraw();
    };

    const toggleActive = async (withdraw: WithdrawType) => {
        const partnerId = sessionStorage.getItem("partnerId");
        if (!partnerId) return;

        setLoadingToggleMap((prev) => ({ ...prev, [withdraw.id]: true }));

        try {
            await updateDoc(doc(db, `partner/${partnerId}/withdraw`, withdraw.id), {
                active: !withdraw.active,
            });
        } catch (err) {
            console.error(err);
            alert("상태 변경에 실패했습니다.");
        } finally {
            setLoadingToggleMap((prev) => ({ ...prev, [withdraw.id]: false }));
        }
    };

    return (
        <>
            {withdraws && (
                <>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이미지</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>제목</p>
                        {/* <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>유형</p> */}
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>금액</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>활성</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>편집</p>
                    </div>
                    <div className={style["wrapper--point-list"]}>
                        {withdraws.map((withdraw) => (
                            <div key={withdraw.id} className={style["unit--point-list"]}>
                                <div className={style["container--rows"]}>
                                    <div className={style["thumbnail"]}>
                                        {withdraw.image ? (
                                            <img
                                                src={withdraw.image}
                                                alt={withdraw.title}
                                                style={{ width: 40, height: 40, objectFit: "cover" }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    backgroundColor: "#f0f0f0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "10px",
                                                }}
                                            >
                                                없음
                                            </div>
                                        )}
                                    </div>
                                    <p className={style["text--eyebrow"]}>{withdraw.title}</p>
                                    {/* <p className={style["text--eyebrow"]}>{withdraw.type}</p> */}
                                    <p className={style["text--eyebrow"]}>
                                        {Number(withdraw.amount).toLocaleString()}원
                                    </p>
                                    <ToggleSwitch
                                        checked={withdraw.active}
                                        onChange={() => toggleActive(withdraw)}
                                        disabled={loadingToggleMap[withdraw.id]}
                                    />
                                    <BlockButton type="Default" text="편집하기" onClick={() => editInfo(withdraw)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const AddWithdraw = ({ close }: { close: () => void }) => {
    const init = useRef(true);

    const [title, setTitle] = useState<string>("");
    const [titleError, setTitleError] = useState<string>("");
    const [amount, setAmount] = useState<string>("");
    const [amountError, setAmountError] = useState<string>("");
    const [typeValue, setTypeValue] = useState<string>("COUPON");
    const [activeFlag, setActiveFlag] = useState<boolean>(true);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoError, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // 제목 유효성 검사
    useEffect(() => {
        if (init.current) return;

        if (!title.trim()) {
            setTitleError("제목을 입력해주세요.");
        } else if (title.trim().length < 2) {
            setTitleError("제목은 2글자 이상 입력해주세요.");
        } else {
            setTitleError("");
        }
    }, [title]);

    // 금액 유효성 검사
    useEffect(() => {
        if (init.current) return;

        if (!amount || Number(amount) <= 0) {
            setAmountError("올바른 금액을 입력해주세요.");
        } else {
            setAmountError("");
        }
    }, [amount]);

    // 사진 유효성 검사
    useEffect(() => {
        if (init.current) return;

        if (!photo) {
            setPhotoError("이미지를 업로드해주세요.");
        } else {
            setPhotoError("");
        }
    }, [photo]);

    useEffect(() => {
        if (init.current) {
            init.current = false;
        }
    }, []);

    const [overlayActive, setOverlayActive] = useState<boolean>(false);
    useEffect(() => {
        setOverlayActive(true);
    }, []);

    const hide = () => {
        setOverlayActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    const saveWithdraw = async () => {
        // 최종 유효성 검사
        if (!title.trim()) {
            setTitleError("제목을 입력해주세요.");
            return;
        }
        if (!amount || Number(amount) <= 0) {
            setAmountError("올바른 금액을 입력해주세요.");
            return;
        }
        if (!photo) {
            setPhotoError("이미지를 업로드해주세요.");
            return;
        }

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            alert("로그인 상태가 아닙니다.");
            return;
        }

        setLoading(true);

        try {
            await user.getIdToken(true);
            const token = await getIdTokenResult(user, true);
            const claimPartnerId = (token.claims as any)?.partnerId as string | undefined;
            const claimType = (token.claims as any)?.type as string | undefined;

            if (claimType !== "GROUP_ADMIN" || !claimPartnerId) {
                setLoading(false);
                alert("권한이 없거나 partnerId 정보를 확인할 수 없습니다.");
                return;
            }

            const colRef = collection(db, `partner/${claimPartnerId}/withdraw`);
            const docRef = doc(colRef);

            const timestamp = Date.now();

            await setDoc(docRef, {
                id: docRef.id,
                title: title.trim(),
                amount: Number(amount),
                type: typeValue,
                active: activeFlag,
                image: "",
                timestamp,
            });

            const resized = await photoResize(photo, "0");
            const storageRef = ref(storage, `partner/${claimPartnerId}/withdraw/${docRef.id}.png`);
            const uploadTask = uploadBytesResumable(storageRef, resized);

            await new Promise<void>((resolve, reject) => {
                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setProgress(prog);
                    },
                    (error) => reject(error),
                    () => resolve()
                );
            });

            const url = await getDownloadURL(ref(storage, `partner/${claimPartnerId}/withdraw/${docRef.id}.png`));
            await updateDoc(docRef, { image: url });

            setLoading(false);
            alert("환전 방법이 추가되었습니다.");
            hide();
        } catch (err) {
            console.error(err);
            setLoading(false);
            alert("에러가 발생했습니다");
        }
    };

    return (
        <div
            className={[common["container--overlay"], overlayActive && common["container--overlay--active"]].join(" ")}
        >
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>환전 방법 추가</h4>
            <div className={[common["unit--overlay"], overlayActive && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay"]}>
                        <div className={style["wrapper--overlay--inputs"]}>
                            <h4 className={common["text--overlay"]}>기본 정보</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput
                                    type="text"
                                    placeholder="제목"
                                    value={title}
                                    setValue={setTitle}
                                    error={titleError}
                                />
                                <BlockInput
                                    type="number"
                                    placeholder="필요 포인트(금액)"
                                    value={amount}
                                    setValue={setAmount}
                                    error={amountError}
                                />
                                <div
                                    className={style["unit--member-title"]}
                                    style={{ gap: 8, display: "flex", alignItems: "center" }}
                                >
                                    <span className={style["text--member"]}>사용 여부</span>
                                    <ToggleSwitch checked={activeFlag} onChange={() => setActiveFlag(!activeFlag)} />
                                </div>
                            </div>
                        </div>
                        <div className={style["wrapper--overlay--photo"]}>
                            <h4 className={common["text--overlay"]}>이미지</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <PhotoInput
                                    photo={photo}
                                    setPhoto={(file) => setPhoto(file)}
                                    progress={progress}
                                    error={photoError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                    <ListButton text="추가하기" loading={loading} onClick={saveWithdraw} />
                </div>
            </div>
        </div>
    );
};

const EditWithdraw = ({ withdraw, close }: { withdraw: WithdrawType; close: () => void }) => {
    const init = useRef(true);

    const [title, setTitle] = useState<string>(withdraw.title || "");
    const [titleError, setTitleError] = useState<string>("");
    const [amount, setAmount] = useState<string>(withdraw.amount || "");
    const [amountError, setAmountError] = useState<string>("");
    const [typeValue, setTypeValue] = useState<string>(withdraw.type || "COUPON");
    const [activeFlag, setActiveFlag] = useState<boolean>(withdraw.active || true);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoError, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // 제목 유효성 검사
    useEffect(() => {
        if (init.current) return;

        if (!title.trim()) {
            setTitleError("제목을 입력해주세요.");
        } else if (title.trim().length < 2) {
            setTitleError("제목은 2글자 이상 입력해주세요.");
        } else {
            setTitleError("");
        }
    }, [title]);

    // 금액 유효성 검사
    useEffect(() => {
        if (init.current) return;

        if (!amount || Number(amount) <= 0) {
            setAmountError("올바른 금액을 입력해주세요.");
        } else {
            setAmountError("");
        }
    }, [amount]);

    useEffect(() => {
        if (init.current) {
            init.current = false;
        }
    }, []);

    const [overlayActive, setOverlayActive] = useState<boolean>(false);
    useEffect(() => {
        setOverlayActive(true);
    }, []);

    const hide = () => {
        setOverlayActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    const updateWithdraw = async () => {
        // 최종 유효성 검사
        if (!title.trim()) {
            setTitleError("제목을 입력해주세요.");
            return;
        }
        if (!amount || Number(amount) <= 0) {
            setAmountError("올바른 금액을 입력해주세요.");
            return;
        }

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            alert("로그인 상태가 아닙니다.");
            return;
        }

        setLoading(true);

        try {
            await user.getIdToken(true);
            const token = await getIdTokenResult(user, true);
            const claimPartnerId = (token.claims as any)?.partnerId as string | undefined;
            const claimType = (token.claims as any)?.type as string | undefined;

            if (claimType !== "GROUP_ADMIN" || !claimPartnerId) {
                setLoading(false);
                alert("권한이 없거나 partnerId 정보를 확인할 수 없습니다.");
                return;
            }

            let imageUrl = withdraw.image; // 기존 이미지 URL 유지

            // 새 이미지가 업로드된 경우에만 처리
            if (photo) {
                const resized = await photoResize(photo, "0");
                const storageRef = ref(storage, `partner/${claimPartnerId}/withdraw/${withdraw.id}.png`);
                const uploadTask = uploadBytesResumable(storageRef, resized);

                await new Promise<void>((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setProgress(prog);
                        },
                        (error) => reject(error),
                        () => resolve()
                    );
                });

                imageUrl = await getDownloadURL(ref(storage, `partner/${claimPartnerId}/withdraw/${withdraw.id}.png`));
            }

            await updateDoc(doc(db, `partner/${claimPartnerId}/withdraw`, withdraw.id), {
                title: title.trim(),
                amount: amount,
                type: typeValue,
                active: activeFlag,
                image: imageUrl,
            });

            setLoading(false);
            alert("환전 방법이 수정되었습니다.");
            hide();
        } catch (err) {
            console.error(err);
            setLoading(false);
            alert("에러가 발생했습니다");
        }
    };

    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deleteWithdraw = async () => {
        setLoadingDelete(true);
        try {
            const claimPartnerId = sessionStorage.getItem("partnerId");
            if (!claimPartnerId) {
                alert("파트너 정보를 찾을 수 없습니다.");
                return;
            }

            // Storage에서 이미지 삭제
            if (withdraw.image) {
                try {
                    const imageRef = ref(storage, `partner/${claimPartnerId}/withdraw/${withdraw.id}.png`);
                    await deleteObject(imageRef);
                } catch (storageError) {
                    console.warn("이미지 삭제 중 오류 발생 (계속 진행):", storageError);
                    // 이미지 삭제 실패해도 문서 삭제는 계속 진행
                }
            }

            // Firestore 문서 삭제
            await deleteDoc(doc(db, `partner/${claimPartnerId}/withdraw`, withdraw.id));
            alert("삭제되었습니다.");
            hide();
        } catch (error) {
            console.error("환전 방법 삭제 중 오류 발생:", error);
            alert("삭제 중 오류가 발생했습니다. 다시 시도해 주세요.");
        } finally {
            setLoadingDelete(false);
        }
    };

    return (
        <div
            className={[common["container--overlay"], overlayActive && common["container--overlay--active"]].join(" ")}
        >
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>환전 방법 수정</h4>
            <div className={[common["unit--overlay"], overlayActive && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay"]}>
                        <div className={style["wrapper--overlay--inputs"]}>
                            <h4 className={common["text--overlay"]}>기본 정보</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput
                                    type="text"
                                    placeholder="제목"
                                    value={title}
                                    setValue={setTitle}
                                    error={titleError}
                                />
                                <BlockInput
                                    type="number"
                                    placeholder="필요 포인트(금액)"
                                    value={amount}
                                    setValue={setAmount}
                                    error={amountError}
                                />

                                <ConfirmButton
                                    type="Delete"
                                    loading={loadingDelete}
                                    onClick={deleteWithdraw}
                                    text="삭제"
                                />
                            </div>
                        </div>
                        <div className={style["wrapper--overlay--photo"]}>
                            <h4 className={common["text--overlay"]}>이미지</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <PhotoInput
                                    photo={photo}
                                    setPhoto={(file) => setPhoto(file)}
                                    progress={progress}
                                    error={photoError}
                                    existingImageUrl={withdraw.image}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                    <ListButton text="수정하기" loading={loading} onClick={updateWithdraw} />
                </div>
            </div>
        </div>
    );
};

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
};

export default Point;
